import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Languages,
  Leaf,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useLoginContext } from "../Contexts/LoginContext";
import {
  completeOtpOnboarding,
  ONBOARDING_IDENTIFIER_KEY,
  ONBOARDING_TOKEN_KEY,
} from "../services/authService";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, loggedIn } = useLoginContext();
  const [name, setName] = useState("");
  const [language, setLanguage] = useState(() =>
    i18n.resolvedLanguage?.toLowerCase().startsWith("hi") ? "hi" : "en",
  );
  const [role, setRole] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const onboardingToken = useMemo(
    () => sessionStorage.getItem(ONBOARDING_TOKEN_KEY),
    [],
  );
  const maskedIdentifier = useMemo(
    () => sessionStorage.getItem(ONBOARDING_IDENTIFIER_KEY) || "",
    [],
  );

  useEffect(() => {
    if (loggedIn) navigate("/", { replace: true });
  }, [loggedIn, navigate]);

  useEffect(() => {
    if (!onboardingToken) setSessionExpired(true);
  }, [onboardingToken]);

  const selectLanguage = async (nextLanguage) => {
    setLanguage(nextLanguage);
    await i18n.changeLanguage(nextLanguage);
    localStorage.setItem("i18nextLng", nextLanguage);
    document.documentElement.lang = nextLanguage;
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setError(t("onboarding.name_required"));
      return;
    }
    if (!role) {
      setError(t("onboarding.role_required"));
      return;
    }
    if (!acceptedTerms) {
      setError(t("onboarding.terms_required"));
      return;
    }
    if (!onboardingToken) {
      setSessionExpired(true);
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      const data = await completeOtpOnboarding({
        onboardingToken,
        name: name.trim(),
        role,
        language,
        acceptedTerms,
      });

      if (!data?.token) {
        setError(t("onboarding.complete_error"));
        return;
      }

      sessionStorage.removeItem(ONBOARDING_TOKEN_KEY);
      sessionStorage.removeItem(ONBOARDING_IDENTIFIER_KEY);
      login(data.token);
      navigate("/", { replace: true });
    } catch (submitError) {
      if ([401, 410].includes(submitError?.response?.status)) {
        sessionStorage.removeItem(ONBOARDING_TOKEN_KEY);
        sessionStorage.removeItem(ONBOARDING_IDENTIFIER_KEY);
        setSessionExpired(true);
      } else {
        setError(
          getApiError(submitError, t("onboarding.complete_error")),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (sessionExpired) {
    return (
      <main className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-emerald-50 px-4 py-8 text-[#1A2233]">
        <section className="w-full max-w-md rounded-3xl border border-white bg-white p-8 text-center shadow-xl shadow-indigo-100/70">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
            <ShieldCheck className="h-7 w-7" aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-slate-900">
            {t("onboarding.session_expired")}
          </h1>
          <Link
            to="/login"
            className="mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            {t("auth.otp_link")}
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen w-full bg-gradient-to-br from-indigo-50 via-white to-emerald-50 px-4 py-8 text-[#1A2233] sm:py-12">
      <section className="mx-auto w-full max-w-2xl rounded-3xl border border-white/70 bg-white p-6 shadow-xl shadow-indigo-100/70 sm:p-9">
        <header className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-200">
            <UserRound className="h-7 w-7" aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-slate-900 sm:text-3xl">
            {t("onboarding.title")}
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600">
            {t("onboarding.subtitle")}
          </p>
          {maskedIdentifier && (
            <p className="mt-2 text-xs font-semibold text-emerald-700">
              {maskedIdentifier}
            </p>
          )}
        </header>

        <div
          id="onboarding-error"
          className={`mt-5 rounded-xl border px-4 py-3 text-sm ${
            error
              ? "border-red-200 bg-red-50 text-red-700"
              : "sr-only"
          }`}
          role="status"
          aria-live="polite"
        >
          {error}
        </div>

        <form className="mt-7 space-y-7" onSubmit={handleSubmit}>
          <fieldset>
            <legend className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <Languages className="h-5 w-5 text-indigo-600" aria-hidden="true" />
              {t("onboarding.language_label")}
            </legend>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {[
                { value: "en", label: t("onboarding.english") },
                { value: "hi", label: t("onboarding.hindi") },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={language === option.value}
                  onClick={() => selectLanguage(option.value)}
                  className={`min-h-12 rounded-xl border px-4 py-3 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    language === option.value
                      ? "border-indigo-600 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-600"
                      : "border-slate-300 bg-white text-slate-700 hover:border-indigo-300"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div>
            <label
              htmlFor="onboarding-name"
              className="mb-2 block text-sm font-bold text-slate-800"
            >
              {t("onboarding.name_label")}
            </label>
            <input
              id="onboarding-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setError("");
              }}
              maxLength={80}
              placeholder={t("onboarding.name_placeholder")}
              className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <fieldset>
            <legend className="text-sm font-bold text-slate-800">
              {t("onboarding.role_label")}
            </legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {[
                {
                  value: "admin",
                  label: t("onboarding.dairy_owner"),
                  description: t("onboarding.dairy_owner_desc"),
                  Icon: Building2,
                },
                {
                  value: "user",
                  label: t("onboarding.farmer"),
                  description: t("onboarding.farmer_desc"),
                  Icon: Leaf,
                },
              ].map(({ value, label, description, Icon }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={role === value}
                  onClick={() => {
                    setRole(value);
                    setError("");
                  }}
                  className={`rounded-2xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    role === value
                      ? "border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600"
                      : "border-slate-300 bg-white hover:border-indigo-300 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                        role === value
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block font-bold text-slate-900">
                        {label}
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-600">
                        {description}
                      </span>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </fieldset>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => {
                setAcceptedTerms(event.target.checked);
                setError("");
              }}
              className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              {t("onboarding.terms_prefix")} {" "}
              <Link
                to="/PrivacyPolicy"
                target="_blank"
                className="font-bold text-indigo-700 hover:underline"
              >
                {t("onboarding.privacy_policy")}
              </Link>{" "}
              {t("onboarding.terms_and")} {" "}
              <Link
                to="/PrivacyPolicy#terms"
                target="_blank"
                className="font-bold text-indigo-700 hover:underline"
              >
                {t("onboarding.terms_service")}
              </Link>
            </span>
          </label>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting
              ? t("onboarding.submitting")
              : t("onboarding.submit")}
          </button>
        </form>
      </section>
    </main>
  );
}

export default OnboardingPage;
